/**
 * Hook for grouping registries by year for tree view display.
 * Extracts year from registry names and organizes into hierarchical groups.
 */

import type { Registry } from "@eoln/sdk-invoices";
import { useMemo } from "react";

/**
 * A group of registries for a specific year.
 */
export interface RegistryGroup {
  /** The year (e.g., 2025) or 0 for registries without a year */
  year: number;
  /** Registries belonging to this year group */
  registries: Registry[];
  /** Total invoice count for all registries in this group */
  totalCount: number;
}

/**
 * Extracts a 4-digit year (20xx) from a registry name.
 *
 * @param name - The registry name to parse (e.g., "VAT 2025/01", "Receipt 2024")
 * @returns The extracted year or null if no year found
 *
 * @example
 * extractYear("VAT 2025/01") // 2025
 * extractYear("Proforma 2024") // 2024
 * extractYear("Legacy Registry") // null
 */
export function extractYear(name: string): number | null {
  // Match 4-digit years starting with 20 (2000-2099)
  const match = name.match(/\b(20\d{2})\b/);
  return match ? Number.parseInt(match[1], 10) : null;
}

/**
 * Groups registries by year for tree view display.
 *
 * @param registries - Array of registries to group
 * @param invoiceCounts - Map of registryId to invoice count
 * @returns Array of RegistryGroup sorted by year descending (newest first)
 *
 * @example
 * const groups = useRegistryTree(registries, invoiceCounts);
 * // Returns: [{ year: 2025, registries: [...], totalCount: 45 }, ...]
 */
export function useRegistryTree(
  registries: Registry[] | undefined,
  invoiceCounts: Map<string, number>,
): RegistryGroup[] {
  return useMemo(() => {
    if (!registries || registries.length === 0) {
      return [];
    }

    // Group registries by year
    const groupMap = new Map<number, Registry[]>();

    registries.forEach((registry) => {
      const year = extractYear(registry.name) ?? 0; // 0 for unknown/other
      const existing = groupMap.get(year) ?? [];
      groupMap.set(year, [...existing, registry]);
    });

    // Convert to array, calculate totals, and sort
    const groups: RegistryGroup[] = Array.from(groupMap.entries()).map(([year, regs]) => ({
      year,
      // Sort registries within group alphabetically by name
      registries: regs.sort((a, b) => a.name.localeCompare(b.name)),
      // Sum invoice counts for all registries in this group
      totalCount: regs.reduce((sum, r) => sum + (invoiceCounts.get(r.registryId) ?? 0), 0),
    }));

    // Sort groups by year descending (newest first), with "Other" (year=0) at the end
    return groups.sort((a, b) => {
      // Both are "Other" - keep original order
      if (a.year === 0 && b.year === 0) {
        return 0;
      }
      // Only a is "Other" - move to end
      if (a.year === 0) {
        return 1;
      }
      // Only b is "Other" - a comes first
      if (b.year === 0) {
        return -1;
      }
      // Both have years - sort descending
      return b.year - a.year;
    });
  }, [registries, invoiceCounts]);
}
